import React from 'react';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import FpaSearchParams from './FpaSearchParams';
import FreelancerPayableAssignmentsService from '../../../Services/FreelancerPayableAssignmentsService';
import OrderDirectionsEnum from '../../../Enums/OrderDirectionsEnum';
import FreelancerPayableAssignment from '../../../Domain/FreelancerPayableAssignmentsItem';
import ErrorMessage from '../../../Elements/ErrorMessage';
import stringHelper from '../../../Helpers/stringHelper';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';
import FigmaButtonRow from '../../../Elements/FigmaButtonRow';
import AuthService from '../../../Services/authService';
import UserService from '../../../Services/userService';
import { buildBulkActionResult } from '../../../Helpers/bulkActionMessageHelper';

// The statuses each bulk action accepts. The backend re-checks them and acts only on the rows that
// still qualify, so the same list has to gate what the client counts, confirms and sends.
const ADD_TO_INVOICE_STATUSES = ['ReadyForInvoicing'];
const REMOVE_FROM_INVOICE_STATUSES = ['OnDraftInvoice'];
const DETACH_FROM_FEE_STATUSES = ['ReadyForInvoicing', 'OnDraftInvoice'];
const SET_UNINVOICEABLE_STATUSES = [
  'NoFee',
  'ReadyForInvoicing',
  'AssignmentExcludedExcl',
  'AssignmentCreated',
  'AssignmentCompleted',
];
const SET_INVOICEABLE_STATUSES = ['Uninvoiceable'];
const ASSIGN_FEE_STATUSES = ['NoFee'];

class FpaAdmin extends React.Component {
  static defaultProps = {};

  constructor(props) {
    super(props);
    const tempSearchParams = new FpaSearchParams();
    if (props.freelancerInvoiceId) {
      tempSearchParams.FreelancerInvoiceId = props.freelancerInvoiceId.toString();
      tempSearchParams.InvoicingStatus = [];
      tempSearchParams.OfficeId = '';
    }
    if (props.freelancerFeeId) {
      tempSearchParams.FreelancerFeeId = props.freelancerFeeId.toString();
      tempSearchParams.InvoicingStatus = [];
      tempSearchParams.OfficeId = '';
    }
    if (props.freelancerUserId) {
      tempSearchParams.FreelancerUserIdOrName = props.freelancerUserId.toString();
      tempSearchParams.InvoicingStatus = [];
      tempSearchParams.OfficeId = '';
    }
    this.state = {
      searchItem: tempSearchParams,
      shownPanel: props.showSearchPanel ? ShownPanelsEnum.Search : ShownPanelsEnum.None,
      selected: -1,
      itemList: [],
      selectedItem: null,
      numberOfElementsInTheTable: 0,
      range: 20,
      allItemsInTheSearchSetAreSelected: false,
      // The grid keeps the selection while the user pages through the list, so the actions read it
      // from here and not from the page currently loaded.
      selectedItems: [],
    };

    this.refresh = this.refresh.bind(this);
    this.startSearch = this.startSearch.bind(this);
    this.clickOnAddToInvoiceButton = this.clickOnAddToInvoiceButton.bind(this);
    this.clickOnDetachFromTheFeeButton = this.clickOnDetachFromTheFeeButton.bind(this);
    this.clickOnLookingForFeeButton = this.clickOnLookingForFeeButton.bind(this);
    this.clickOnRemoveFromInvoiceButton = this.clickOnRemoveFromInvoiceButton.bind(this);
    this.clickOnUninvoiceableButton = this.clickOnUninvoiceableButton.bind(this);
    this.clickOnInvoiceableButton = this.clickOnInvoiceableButton.bind(this);
    this.handleExportExcelButtonClick = this.handleExportExcelButtonClick.bind(this);
    FreelancerPayableAssignment.clickOnRemoveFromInvoiceIcon = this.clickOnRemoveFromInvoiceIcon.bind(this);
  }

  clickOnRemoveFromInvoiceInPopupWindow(freelancerPayableAssignmentsId) {
    FreelancerPayableAssignmentsService.RemovePayableAssignmentFromFreelancerInvoice(freelancerPayableAssignmentsId)
      .then((result) => {
        this.refresh();
      })
      .catch((error) => {
        ErrorMessage.SetErrorMessage('FPAA164');
      });
  }

  refresh() {
    this.setState({ searchItem: { ...this.state.searchItem } });
  }

  editPanelOnClose(refresh) {
    this.setState({
      shownPanel: ShownPanelsEnum.None,
    });
    if (refresh) {
      this.refresh();
    }
  }

  clickOnRemoveFromInvoiceIcon(freelancerPayableAssignmentsId) {
    ErrorMessage.SetApprove(
      `You will remove this assignment (${freelancerPayableAssignmentsId}) from the invoice. Are you sure?`,
      this.clickOnRemoveFromInvoiceInPopupWindow.bind(this)
    );
  }

  showPanel(key) {
    if (key === ShownPanelsEnum.Refresh) {
      this.refresh();
      key = ShownPanelsEnum.None;
    }
    if (key === this.state.shownPanel) key = ShownPanelsEnum.None;
    this.setState({ shownPanel: key });
  }

  selectedIdsWithStatus(statuses) {
    return this.state.selectedItems
      .filter((i) => statuses.includes(i.Status))
      .map((f) => f.FreelancerPayableAssignmentsId);
  }

  // Shows the outcome of a bulk action: the backend acts only on the rows still in an eligible
  // status, so a smaller count than sent is a warning about a stale selection, not a success.
  showBulkActionResult(done, requestedIds, actionDescription, note = '') {
    const result = buildBulkActionResult({
      done,
      requested: this.state.allItemsInTheSearchSetAreSelected ? null : requestedIds.length,
      allInSetSelected: this.state.allItemsInTheSearchSetAreSelected,
      actionDescription,
    });
    const message = note ? `${result.message} ${note}` : result.message;
    if (result.isSuccess) {
      ErrorMessage.SetSuccess(message);
    } else {
      ErrorMessage.SetWarning(message);
    }
  }

  clickOnAddToInvoiceButton() {
    const numberOfAssignments = this.state.allItemsInTheSearchSetAreSelected
      ? this.state.numberOfElementsInTheTable
      : this.selectedIdsWithStatus(ADD_TO_INVOICE_STATUSES).length;
    if (numberOfAssignments > 0) {
      ErrorMessage.SetApprove(
        `You will attach ${stringHelper.plural(
          numberOfAssignments,
          'assignment'
        )} to a Vendor Invoice. (Only assigments in the ReadyForInvoicing status.)`,
        this.clickOnAddToInvoiceButtonInPopup.bind(this)
      );
    } else {
      ErrorMessage.SetWarning("You haven't selected any valid assignments for this action.");
    }
  }

  clickOnAddToInvoiceButtonInPopup() {
    const selectedIds = this.selectedIdsWithStatus(ADD_TO_INVOICE_STATUSES);
    if (!this.state.allItemsInTheSearchSetAreSelected && selectedIds.length === 0) {
      ErrorMessage.SetWarning("You haven't selected any valid assignments for this action.");
      return;
    }
    FreelancerPayableAssignmentsService.AttachFreelancerPayableAssignmentsToFreelancerInvoice(
      selectedIds,
      this.state.allItemsInTheSearchSetAreSelected,
      this.state.searchItem
    )
      .then((result) => {
        this.refresh();
        this.showBulkActionResult(result, selectedIds, 'attached to a vendor invoice');
      })
      .catch((error) => {
        ErrorMessage.SetErrorMessage('FPAA301');
      });
  }

  clickOnDetachFromTheFeeButton() {
    const numberOfAssignments = this.state.allItemsInTheSearchSetAreSelected
      ? this.state.numberOfElementsInTheTable
      : this.selectedIdsWithStatus(DETACH_FROM_FEE_STATUSES).length;
    if (numberOfAssignments > 0) {
      ErrorMessage.SetApprove(
        `You will detach ${stringHelper.plural(
          numberOfAssignments,
          'assignment'
        )} from their fee. (Only assigments in the ReadyForInvoicing or OnDraftInvoice status.)`,
        this.clickOnDetachFromFeeButtonInPopup.bind(this)
      );
    } else {
      ErrorMessage.SetWarning("You haven't selected any valid assignments for this action.");
    }
  }

  clickOnDetachFromFeeButtonInPopup() {
    const selectedIds = this.selectedIdsWithStatus(DETACH_FROM_FEE_STATUSES);
    FreelancerPayableAssignmentsService.DetachFreelancerPayableAssignmentsFromFee(
      selectedIds,
      this.state.allItemsInTheSearchSetAreSelected,
      this.state.searchItem
    )
      .then((result) => {
        this.refresh();
        this.showBulkActionResult(result, selectedIds, 'detached from the fee');
      })
      .catch((error) => {
        ErrorMessage.SetErrorMessage('FPAA369');
      });
  }

  clickOnRemoveFromInvoiceButton() {
    const numberOfAssignments = this.state.allItemsInTheSearchSetAreSelected
      ? this.state.numberOfElementsInTheTable
      : this.selectedIdsWithStatus(REMOVE_FROM_INVOICE_STATUSES).length;
    if (numberOfAssignments > 0) {
      ErrorMessage.SetApprove(
        `You will remove ${stringHelper.plural(
          numberOfAssignments,
          'assignment'
        )} from their invoice. (Only assigments on draft invoice.)`,
        this.clickOnRemoveFromInvoiceButtonInPopup.bind(this)
      );
    } else {
      ErrorMessage.SetWarning("You haven't selected any valid assignments for this action.");
    }
  }

  clickOnRemoveFromInvoiceButtonInPopup() {
    const selectedIds = this.selectedIdsWithStatus(REMOVE_FROM_INVOICE_STATUSES);
    FreelancerPayableAssignmentsService.RemoveFreelancerPayableAssignmentsFromInvoice(
      selectedIds,
      this.state.allItemsInTheSearchSetAreSelected,
      this.state.searchItem
    )
      .then((result) => {
        this.refresh();
        this.showBulkActionResult(result, selectedIds, 'removed from the invoice');
      })
      .catch((error) => ErrorMessage.SetErrorMessage('FPAA438'));
  }

  clickOnUninvoiceableButton() {
    const numberOfAssignments = this.state.allItemsInTheSearchSetAreSelected
      ? this.state.numberOfElementsInTheTable
      : this.selectedIdsWithStatus(SET_UNINVOICEABLE_STATUSES).length;

    if (numberOfAssignments > 0) {
      const message = (
        <span>
          You will set {stringHelper.plural(numberOfAssignments, 'assignment')} to uninvoiceable. (Only assigments in
          the following statuses: NoFee, ReadyForInvoicing, ExcludedExcl., AssignmentCreated or AssignmentCompleted )
        </span>
      );
      ErrorMessage.SetApprove(message, this.clickOnUninvoiceableButtonInPopup.bind(this));
    } else {
      ErrorMessage.SetWarning("You haven't selected any valid assignments for this action.");
    }
  }

  clickOnUninvoiceableButtonInPopup() {
    const selectedIds = this.selectedIdsWithStatus(SET_UNINVOICEABLE_STATUSES);
    FreelancerPayableAssignmentsService.SetFreelancerPayableAssignmentsToUninvoiceable(
      selectedIds,
      this.state.allItemsInTheSearchSetAreSelected,
      this.state.searchItem
    )
      .then((result) => {
        this.refresh();
        this.showBulkActionResult(result, selectedIds, 'set to Uninvoiceable');
      })
      .catch((error) => {
        ErrorMessage.SetErrorMessage('FPAA443');
      });
  }

  clickOnInvoiceableButton() {
    const numberOfAssignments = this.state.allItemsInTheSearchSetAreSelected
      ? this.state.numberOfElementsInTheTable
      : this.selectedIdsWithStatus(SET_INVOICEABLE_STATUSES).length;
    if (numberOfAssignments > 0) {
      ErrorMessage.SetApprove(
        `You will set ${stringHelper.plural(
          numberOfAssignments,
          'assignment'
        )} to invoiceable. (Only assigments in the uninvoiceable status.)`,
        this.clickOnInvoiceableButtonInPopup.bind(this)
      );
    } else {
      ErrorMessage.SetWarning("You haven't selected any valid assignments for this action.");
    }
  }

  clickOnInvoiceableButtonInPopup() {
    const selectedIds = this.selectedIdsWithStatus(SET_INVOICEABLE_STATUSES);
    FreelancerPayableAssignmentsService.SetFreelancerPayableAssignmentsToInvoiceable(
      selectedIds,
      this.state.allItemsInTheSearchSetAreSelected,
      this.state.searchItem
    )
      .then((result) => {
        this.refresh();
        this.showBulkActionResult(
          result,
          selectedIds,
          'set to invoiceable',
          'The Payable Assignment Status depends on related details.'
        );
      })
      .catch((error) => {
        ErrorMessage.SetErrorMessage('FPAA579');
      });
  }

  clickOnLookingForFeeButton() {
    const numberOfAssignments = this.state.allItemsInTheSearchSetAreSelected
      ? this.state.numberOfElementsInTheTable
      : this.state.selectedItems.filter(
          (i) =>
            ASSIGN_FEE_STATUSES.includes(i.Status) && (i.SameIndividual !== true || i.FreelancerUserPayIfSame === true)
        ).length;
    if (numberOfAssignments > 0) {
      ErrorMessage.SetApprove(
        `Start fee linking process for ${stringHelper.plural(
          numberOfAssignments,
          'assignment'
        )}. Process considers only assignments where the status is "NoFee" and the Evaluator and the Vendor is not the same.
        (except if the vendor is allowed to receive payments for its own assignments)`,
        this.clickOnLookingForFeeButtonInPopup.bind(this)
      );
    } else {
      ErrorMessage.SetWarning("You haven't selected any valid assignments for this action.");
    }
  }

  clickOnLookingForFeeButtonInPopup() {
    const allSelected = this.selectedIdsWithStatus(ASSIGN_FEE_STATUSES);
    FreelancerPayableAssignmentsService.LookingForFeeToFreelancerPayableAssignments(
      allSelected,
      this.state.allItemsInTheSearchSetAreSelected,
      this.state.searchItem
    )
      .then((result) => {
        this.refresh();
      })
      .catch((error) => {
        ErrorMessage.SetErrorMessage('FPAA647');
      });
  }

  handleExportExcelButtonClick() {
    FreelancerPayableAssignmentsService.ExportAdminExcelAsync(this.state.searchItem).catch(() =>
      ErrorMessage.SetErrorMessage('FPAA334')
    );
    ErrorMessage.SetSuccessWithWait(
      'File creation has started and your report will be available in your personal download menu soon. You can continue to use the system while your request is being processed.'
    );
  }

  IsThereAnySelected() {
    return this.state.selectedItems.length > 0;
  }

  renderActionButtonRow() {
    if (this.state.itemList.length === 0) return null;
    return (
      <FigmaButtonRow
        buttons={[
          {
            iconName: 'fa-solid fa-file-circle-plus',
            type: ButtonTypeEnum.NormalIconOnly,
            color: ButtonColorsEnum.Common,
            iconPosition: IconPositionEnum.Center,
            onClick: this.clickOnAddToInvoiceButton,
            tooltip: 'Add to Invoice',
            disabled: !this.IsThereAnySelected(),
          },
          {
            iconName: 'fa-solid fa-file-circle-xmark',
            type: ButtonTypeEnum.NormalIconOnly,
            color: ButtonColorsEnum.Common,
            iconPosition: IconPositionEnum.Center,
            onClick: this.clickOnRemoveFromInvoiceButton,
            tooltip: 'Remove from Invoice',
            disabled: !this.IsThereAnySelected(),
          },
          {
            iconName: 'fa-solid fa-money-bill-wave',
            type: ButtonTypeEnum.NormalIconOnly,
            color: ButtonColorsEnum.Common,
            iconPosition: IconPositionEnum.Center,
            onClick: this.clickOnLookingForFeeButton,
            tooltip: 'Assign Fee',
            disabled: !this.IsThereAnySelected(),
          },
          {
            iconName: 'fa-solid fa-circle-minus',
            type: ButtonTypeEnum.NormalIconOnly,
            color: ButtonColorsEnum.Common,
            iconPosition: IconPositionEnum.Center,
            onClick: this.clickOnDetachFromTheFeeButton,
            tooltip: 'Remove Fee',
            disabled: !this.IsThereAnySelected(),
          },
          {
            iconName: 'fa-solid fa-file-invoice',
            type: ButtonTypeEnum.NormalIconOnly,
            color: ButtonColorsEnum.Common,
            iconPosition: IconPositionEnum.Center,
            onClick: this.clickOnInvoiceableButton,
            tooltip: 'Set to invoiceable',
            disabled: !this.IsThereAnySelected(),
          },
          {
            iconName: 'fa-solid fa-file-circle-xmark',
            type: ButtonTypeEnum.NormalIconOnly,
            color: ButtonColorsEnum.Common,
            iconPosition: IconPositionEnum.Center,
            onClick: this.clickOnUninvoiceableButton,
            tooltip: 'Set to non-invoiceable',
            disabled: !this.IsThereAnySelected(),
          },
        ]}
      />
    );
  }

  changeOfItemListFunction(itemList, numberOfElementsInTheTable) {
    this.setState({
      itemList,
      numberOfElementsInTheTable,
    });
  }

  startSearch(searchItem) {
    if (
      AuthService.HasRoleArray(['FinanceAdmin_VendorMgtm', 'VendorInvoicingAdmin_*']) &&
      !AuthService.IsAdmin() &&
      !searchItem?.OfficeId
    ) {
      searchItem = { ...searchItem, OfficeId: UserService.GetOfficeIdOfLoggedInUser() };
    }
    this.setState({
      searchItem,
    });
  }

  render() {
    return (
      <div className='contentwrapper'>
        <div className=''>
          {this.renderActionButtonRow()}
          <TabOfDataTable
            type={FreelancerPayableAssignment}
            shownPanel={this.state.shownPanel}
            serviceFunction={FreelancerPayableAssignmentsService.GetAssignmentList}
            convertDBItemsFunction={FreelancerPayableAssignmentsService.ConvertDBItem}
            searchItem={this.state.searchItem}
            searchParamsType={FpaSearchParams}
            orderBy={'FreelancerPayableAssignmentsId'}
            orderDirection={OrderDirectionsEnum.Desc}
            showSearchStrip={true}
            selectable={this.state.shownPanel === ShownPanelsEnum.None}
            clickOnStartSearch={this.startSearch}
            itemList={this.state.itemList}
            changeOfAllItemsAreSelectedFunction={(allItemsInTheSearchSetAreSelected) => {
              this.setState({ allItemsInTheSearchSetAreSelected });
            }}
            itemTypeName={'assignments'}
            retainSelectionAcrossPages={true}
            changeOfSelectionFunction={(selectedItems) => this.setState({ selectedItems })}
            changeOfItemListFunction={this.changeOfItemListFunction.bind(this)}
            errorCode='FA341'
            singleSearchLine={true}
            singleSearchLinePlaceholder='Search for id, vendor, client, survey or invoice...'
            isExportExcelDisabled={false}
            clickOnExportExcel={this.handleExportExcelButtonClick}
            //excelExportRoleKey='reports_freelancerpayableassignmentexport'
          />
        </div>
      </div>
    );
  }
}

export default FpaAdmin;
